import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea, ComposedChart, Area,
} from 'recharts';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

const RANGES = [
  { key: '1D', label: '1D', days: 1 },
  { key: '1W', label: '1W', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: 'ALL', label: 'All', days: null },
];

// HR zones as a warm intensity ramp (gold → orange → red); resting HR is the
// gold "you" value; moving average the green overlay. No blue/pink.
const ZONE_COLORS = {
  outOfRange: '#9ca3af',
  fatBurn: '#FFC21E',
  cardio: '#F0791A',
  peak: '#E2604F',
  resting: '#FFB800',
  movingAvg: '#4FB98A',
};

const LEGEND_LABELS = {
  fatBurn: 'Fat Burn',
  cardio: 'Cardio',
  peak: 'Peak',
  resting: 'Resting HR',
  movingAvg: '7-day Avg',
};

const HeartRateChart = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('1M');

  // Intraday state
  const [intradayData, setIntradayData] = useState(null);
  const [intradayDate, setIntradayDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [intradayLoading, setIntradayLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/api/fitbit/heart-rate');
        if (response.status === 200) setAllData(response.data);
      } catch (error) {
        toast.error('Failed to load heart rate data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch intraday data when 1D is selected or date changes
  useEffect(() => {
    if (range !== '1D') return;
    const fetchIntraday = async () => {
      setIntradayLoading(true);
      setIntradayData(null);
      try {
        const response = await apiClient.get(`/api/fitbit/heart-rate/intraday?date=${intradayDate}`);
        if (response.status === 200) setIntradayData(response.data);
      } catch (error) {
        if (error.response?.status !== 404) {
          toast.error('Failed to load intraday heart rate data');
        }
      } finally {
        setIntradayLoading(false);
      }
    };
    fetchIntraday();
  }, [range, intradayDate]);

  const filteredData = useMemo(() => {
    if (!allData.length) return [];
    const rangeConfig = RANGES.find(r => r.key === range);
    if (!rangeConfig?.days) return allData;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - rangeConfig.days);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    return allData.filter(d => d.date >= cutoffStr);
  }, [allData, range]);

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const latest = filteredData[filteredData.length - 1];
    const avg = (field) => {
      const vals = filteredData.filter(d => d[field] != null).map(d => d[field]);
      return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
    };
    const avgFloat = (field) => {
      const vals = filteredData.filter(d => d[field] != null).map(d => d[field]);
      return vals.length ? parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1)) : null;
    };
    const restingVals = filteredData.filter(d => d.resting_heart_rate != null).map(d => d.resting_heart_rate);
    return {
      latest,
      avgResting: restingVals.length ? Math.round(restingVals.reduce((a, b) => a + b, 0) / restingVals.length) : null,
      minResting: restingVals.length ? Math.min(...restingVals) : null,
      maxResting: restingVals.length ? Math.max(...restingVals) : null,
      avgFatBurn: avg('fat_burn_minutes'),
      avgCardio: avg('cardio_minutes'),
      avgPeak: avg('peak_minutes'),
      avgFatBurnCal: avgFloat('fat_burn_calories'),
      avgCardioCal: avgFloat('cardio_calories'),
      avgPeakCal: avgFloat('peak_calories'),
      totalDays: filteredData.length,
    };
  }, [filteredData]);

  const { chartData, aggregation } = useMemo(() => {
    if (!filteredData.length) return { chartData: [], aggregation: 'daily' };

    let agg = 'daily';
    if (filteredData.length > 365) agg = 'monthly';
    else if (filteredData.length > 90) agg = 'weekly';

    let processed = filteredData;
    if (agg !== 'daily') {
      const groups = {};
      filteredData.forEach(d => {
        const dt = new Date(d.date + 'T00:00:00');
        let key;
        if (agg === 'weekly') {
          const start = new Date(dt);
          start.setDate(dt.getDate() - dt.getDay());
          key = start.toISOString().split('T')[0];
        } else {
          key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
        }
        if (!groups[key]) groups[key] = [];
        groups[key].push(d);
      });

      processed = Object.entries(groups)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([periodStart, items]) => {
          const avg = (field) => {
            const vals = items.filter(i => i[field] != null).map(i => i[field]);
            return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
          };
          const avgFloat = (field) => {
            const vals = items.filter(i => i[field] != null).map(i => i[field]);
            return vals.length ? parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1)) : null;
          };
          return {
            date: periodStart,
            resting_heart_rate: avg('resting_heart_rate'),
            fat_burn_minutes: avg('fat_burn_minutes'),
            cardio_minutes: avg('cardio_minutes'),
            peak_minutes: avg('peak_minutes'),
            out_of_range_minutes: avg('out_of_range_minutes'),
            fat_burn_calories: avgFloat('fat_burn_calories'),
            cardio_calories: avgFloat('cardio_calories'),
            peak_calories: avgFloat('peak_calories'),
            out_of_range_calories: avgFloat('out_of_range_calories'),
          };
        });
    }

    const windowSize = agg === 'daily' ? 7 : agg === 'weekly' ? 4 : 3;
    const result = processed.map((d, i) => {
      const window = processed.slice(Math.max(0, i - windowSize + 1), i + 1);
      const avgVals = window.filter(w => w.resting_heart_rate != null).map(w => w.resting_heart_rate);
      const movingAvg = avgVals.length >= 2
        ? Math.round(avgVals.reduce((a, b) => a + b, 0) / avgVals.length)
        : null;

      return {
        date: d.date,
        resting: d.resting_heart_rate,
        fatBurn: d.fat_burn_minutes || 0,
        cardio: d.cardio_minutes || 0,
        peak: d.peak_minutes || 0,
        outOfRange: d.out_of_range_minutes || 0,
        fatBurnCal: d.fat_burn_calories || 0,
        cardioCal: d.cardio_calories || 0,
        peakCal: d.peak_calories || 0,
        outOfRangeCal: d.out_of_range_calories || 0,
        movingAvg,
      };
    });

    return { chartData: result, aggregation: agg };
  }, [filteredData]);

  // Downsample intraday data to every 5 minutes for performance
  const intradayChartData = useMemo(() => {
    if (!intradayData?.dataset?.length) return [];
    const ds = intradayData.dataset;
    // Sample every 5th point (1440 → ~288)
    const sampled = [];
    for (let i = 0; i < ds.length; i += 5) {
      // Average the next 5 points for smoother line
      const slice = ds.slice(i, i + 5);
      const avg = Math.round(slice.reduce((s, p) => s + p.value, 0) / slice.length);
      sampled.push({ time: ds[i].time, bpm: avg });
    }
    return sampled;
  }, [intradayData]);

  // Zone band colors (semi-transparent for reference areas)
  const ZONE_BAND_COLORS = {
    'Out of Range': 'rgba(156, 163, 175, 0.08)',
    'Fat Burn': 'rgba(234, 179, 8, 0.10)',
    'Cardio': 'rgba(249, 115, 22, 0.12)',
    'Peak': 'rgba(239, 68, 68, 0.14)',
  };

  const toLocalDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const navigateIntradayDate = (direction) => {
    const d = new Date(intradayDate + 'T00:00:00');
    d.setDate(d.getDate() + direction);
    setIntradayDate(toLocalDateStr(d));
  };

  const isToday = intradayDate === toLocalDateStr(new Date());

  const formatIntradayDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!allData.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-500 dark:text-gray-400">
            No heart rate data available. Sync your Fitbit to see heart rate data.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    if (aggregation === 'monthly') {
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    const rangeConfig = RANGES.find(r => r.key === range);
    const days = rangeConfig?.days;
    if (days && days <= 14) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    if (days && days <= 90) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const RestingTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = aggregation === 'monthly'
      ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : aggregation === 'weekly'
        ? `Week of ${new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        {aggregation !== 'daily' && (
          <p className="text-xs text-gray-400 mb-2">({aggregation} average)</p>
        )}
        <div className="space-y-1">
          {d.resting != null && (
            <p style={{ color: ZONE_COLORS.resting }}>
              Resting HR: <span className="font-semibold">{d.resting} bpm</span>
            </p>
          )}
          {d.movingAvg != null && (
            <p style={{ color: ZONE_COLORS.movingAvg }}>
              7-day Avg: <span className="font-semibold">{d.movingAvg} bpm</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  const ZoneTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const totalActive = d.fatBurn + d.cardio + d.peak;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        <div className="space-y-1">
          <p className="text-gray-700 dark:text-gray-300">
            Active Zones: <span className="font-semibold">{totalActive} min</span>
          </p>
          <p style={{ color: ZONE_COLORS.fatBurn }}>
            Fat Burn: <span className="font-semibold">{d.fatBurn} min</span>
          </p>
          <p style={{ color: ZONE_COLORS.cardio }}>
            Cardio: <span className="font-semibold">{d.cardio} min</span>
          </p>
          <p style={{ color: ZONE_COLORS.peak }}>
            Peak: <span className="font-semibold">{d.peak} min</span>
          </p>
        </div>
      </div>
    );
  };

  const CaloriesTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const totalCal = (d.fatBurnCal + d.cardioCal + d.peakCal).toFixed(1);
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{dateLabel}</p>
        <div className="space-y-1">
          <p className="text-gray-700 dark:text-gray-300">
            Zone Calories: <span className="font-semibold">{totalCal}</span>
          </p>
          <p style={{ color: ZONE_COLORS.fatBurn }}>
            Fat Burn: <span className="font-semibold">{d.fatBurnCal.toFixed(1)}</span>
          </p>
          <p style={{ color: ZONE_COLORS.cardio }}>
            Cardio: <span className="font-semibold">{d.cardioCal.toFixed(1)}</span>
          </p>
          <p style={{ color: ZONE_COLORS.peak }}>
            Peak: <span className="font-semibold">{d.peakCal.toFixed(1)}</span>
          </p>
        </div>
      </div>
    );
  };

  const latest = stats?.latest;
  const latestActiveMin = (latest?.fat_burn_minutes || 0) + (latest?.cardio_minutes || 0) + (latest?.peak_minutes || 0);
  const latestZoneTotal = latestActiveMin + (latest?.out_of_range_minutes || 0);

  return (
    <div className="space-y-6">
      {/* Range Selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-1 bg-gray-100 dark:bg-surface-dark-raised rounded-lg p-1">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                range === r.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {range !== '1D' && (
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            {aggregation !== 'daily' && (
              <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs">
                {aggregation} averages
              </span>
            )}
            <span>{stats?.totalDays} days</span>
          </div>
        )}
      </div>

      {/* Intraday View (1D) */}
      {range === '1D' && (
        <>
          {/* Date Navigator */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => navigateIntradayDate(-1)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white min-w-[200px] text-center">
              {formatIntradayDate(intradayDate)}
            </span>
            <button
              onClick={() => navigateIntradayDate(1)}
              disabled={isToday}
              className={`p-1.5 rounded-lg transition-colors ${
                isToday
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400'
              }`}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>

          {intradayLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : !intradayData ? (
            <Card>
              <CardContent className="py-12">
                <p className="text-center text-gray-500 dark:text-gray-400">
                  No intraday heart rate data for this date.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Intraday Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Resting HR</p>
                    <p className="text-2xl font-bold" style={{ color: ZONE_COLORS.resting }}>
                      {intradayData.resting_hr != null ? `${intradayData.resting_hr}` : '--'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">bpm</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Average</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {intradayData.avg_hr != null ? `${intradayData.avg_hr}` : '--'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">bpm</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Min</p>
                    <p className="text-2xl font-bold text-blue-500">
                      {intradayData.min_hr != null ? `${intradayData.min_hr}` : '--'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">bpm</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Max</p>
                    <p className="text-2xl font-bold" style={{ color: ZONE_COLORS.peak }}>
                      {intradayData.max_hr != null ? `${intradayData.max_hr}` : '--'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">bpm</p>
                  </CardContent>
                </Card>
              </div>

              {/* Intraday Line Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Heart Rate — {formatIntradayDate(intradayDate)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={360}>
                    <ComposedChart data={intradayChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                      <XAxis
                        dataKey="time"
                        tickFormatter={(t) => t?.slice(0, 5)}
                        interval={Math.max(0, Math.ceil(intradayChartData.length / 12) - 1)}
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={{ stroke: '#4b5563' }}
                        tickLine={{ stroke: '#4b5563' }}
                      />
                      <YAxis
                        domain={['dataMin - 10', 'dataMax + 10']}
                        tickFormatter={(v) => `${v}`}
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={{ stroke: '#4b5563' }}
                        tickLine={{ stroke: '#4b5563' }}
                        width={42}
                        label={{ value: 'BPM', angle: -90, position: 'insideLeft', style: { fill: '#9ca3af', fontSize: 11 } }}
                      />
                      {/* Zone reference areas */}
                      {intradayData.zones?.map((zone) => (
                        <ReferenceArea
                          key={zone.name}
                          y1={zone.min}
                          y2={zone.max}
                          fill={ZONE_BAND_COLORS[zone.name] || 'rgba(156,163,175,0.05)'}
                          fillOpacity={1}
                          label={{
                            value: zone.name,
                            position: 'insideTopRight',
                            fill: '#9ca3af',
                            fontSize: 10,
                          }}
                        />
                      ))}
                      {/* Resting HR reference line */}
                      {intradayData.resting_hr && (
                        <ReferenceLine
                          y={intradayData.resting_hr}
                          stroke={ZONE_COLORS.resting}
                          strokeDasharray="6 3"
                          label={{
                            value: `Resting ${intradayData.resting_hr}`,
                            position: 'insideTopLeft',
                            fill: ZONE_COLORS.resting,
                            fontSize: 11,
                          }}
                        />
                      )}
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const p = payload[0]?.payload;
                          if (!p) return null;
                          return (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
                              <p className="font-semibold text-gray-900 dark:text-white">{p.time?.slice(0, 5)}</p>
                              <p className="text-gray-700 dark:text-gray-300">
                                <span className="font-semibold" style={{ color: ZONE_COLORS.resting }}>{p.bpm} bpm</span>
                              </p>
                            </div>
                          );
                        }}
                      />
                      <Area
                        dataKey="bpm"
                        stroke={ZONE_COLORS.resting}
                        strokeWidth={1.5}
                        fill={ZONE_COLORS.resting}
                        fillOpacity={0.08}
                        type="monotone"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Multi-day views (all ranges except 1D) */}
      {range !== '1D' && (<>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Resting HR</p>
            <p className="text-2xl font-bold" style={{ color: ZONE_COLORS.resting }}>
              {latest?.resting_heart_rate != null ? `${latest.resting_heart_rate} bpm` : '--'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Avg: {stats?.avgResting != null ? `${stats.avgResting} bpm` : '--'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Active Zone Min</p>
            <p className="text-2xl font-bold" style={{ color: ZONE_COLORS.cardio }}>
              {latestActiveMin > 0 ? `${latestActiveMin} min` : '--'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Avg: {stats?.avgFatBurn != null ? `${(stats.avgFatBurn || 0) + (stats.avgCardio || 0) + (stats.avgPeak || 0)} min` : '--'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Avg Fat Burn</p>
            <p className="text-2xl font-bold" style={{ color: ZONE_COLORS.fatBurn }}>
              {stats?.avgFatBurn != null ? `${stats.avgFatBurn} min` : '--'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {stats?.avgFatBurnCal != null ? `${stats.avgFatBurnCal} cal/day` : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Avg Cardio + Peak</p>
            <p className="text-2xl font-bold" style={{ color: ZONE_COLORS.peak }}>
              {stats?.avgCardio != null ? `${(stats.avgCardio || 0) + (stats.avgPeak || 0)} min` : '--'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {stats?.avgCardioCal != null ? `${((stats.avgCardioCal || 0) + (stats.avgPeakCal || 0)).toFixed(1)} cal/day` : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Latest Zone Breakdown */}
      {latest && latestZoneTotal > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Latest Heart Rate Zones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex rounded-lg overflow-hidden h-8 mb-4">
              {[
                { key: 'outOfRange', val: latest.out_of_range_minutes, color: ZONE_COLORS.outOfRange, label: 'Out of Range' },
                { key: 'fatBurn', val: latest.fat_burn_minutes, color: ZONE_COLORS.fatBurn, label: 'Fat Burn' },
                { key: 'cardio', val: latest.cardio_minutes, color: ZONE_COLORS.cardio, label: 'Cardio' },
                { key: 'peak', val: latest.peak_minutes, color: ZONE_COLORS.peak, label: 'Peak' },
              ].filter(s => s.val > 0).map(s => {
                const pct = (s.val / latestZoneTotal) * 100;
                return (
                  <div
                    key={s.key}
                    style={{ width: `${pct}%`, backgroundColor: s.color }}
                    className="flex items-center justify-center text-xs text-white font-medium"
                    title={`${s.label}: ${s.val} min (${Math.round(pct)}%)`}
                  >
                    {pct > 12 ? `${s.val}m` : ''}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              {[
                { label: 'Out of Range', val: latest.out_of_range_minutes, color: ZONE_COLORS.outOfRange },
                { label: 'Fat Burn', val: latest.fat_burn_minutes, color: ZONE_COLORS.fatBurn },
                { label: 'Cardio', val: latest.cardio_minutes, color: ZONE_COLORS.cardio },
                { label: 'Peak', val: latest.peak_minutes, color: ZONE_COLORS.peak },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">{s.label}</p>
                    <p className="font-semibold text-gray-900 dark:text-white">{s.val != null ? `${s.val} min` : '--'}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Chart: Resting Heart Rate with moving average */}
      <Card>
        <CardHeader>
          <CardTitle>Resting Heart Rate</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                domain={['dataMin - 5', 'dataMax + 5']}
                tickFormatter={(v) => `${v}`}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={42}
              />
              <Tooltip content={<RestingTooltip />} />
              <Legend
                formatter={(value) => LEGEND_LABELS[value] || value}
                wrapperStyle={{ fontSize: 12 }}
              />
              <ReferenceLine
                y={60}
                stroke="#6b7280"
                strokeDasharray="3 3"
                label={{ value: '60 bpm', position: 'insideTopLeft', fill: '#9ca3af', fontSize: 11 }}
              />
              <Line
                dataKey="resting"
                stroke={ZONE_COLORS.resting}
                strokeWidth={2}
                dot={chartData.length <= 60}
                name="resting"
                type="monotone"
                connectNulls
              />
              <Line
                dataKey="movingAvg"
                stroke={ZONE_COLORS.movingAvg}
                strokeWidth={2}
                dot={false}
                name="movingAvg"
                type="monotone"
                connectNulls
                strokeDasharray="5 3"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Zone Minutes (Stacked) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Heart Rate Zone Minutes</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  interval={tickInterval}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  tickFormatter={(v) => `${v}m`}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={40}
                />
                <Tooltip content={<ZoneTooltip />} />
                <Legend
                  formatter={(value) => LEGEND_LABELS[value] || value}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <Bar dataKey="fatBurn" stackId="zones" fill={ZONE_COLORS.fatBurn} name="fatBurn" />
                <Bar dataKey="cardio" stackId="zones" fill={ZONE_COLORS.cardio} name="cardio" />
                <Bar dataKey="peak" stackId="zones" fill={ZONE_COLORS.peak} name="peak" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Zone Calories (Stacked) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Zone Calories Burned</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  interval={tickInterval}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={40}
                />
                <Tooltip content={<CaloriesTooltip />} />
                <Legend
                  formatter={(value) => {
                    const map = { fatBurnCal: 'Fat Burn', cardioCal: 'Cardio', peakCal: 'Peak' };
                    return map[value] || value;
                  }}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <Bar dataKey="fatBurnCal" stackId="cal" fill={ZONE_COLORS.fatBurn} name="fatBurnCal" />
                <Bar dataKey="cardioCal" stackId="cal" fill={ZONE_COLORS.cardio} name="cardioCal" />
                <Bar dataKey="peakCal" stackId="cal" fill={ZONE_COLORS.peak} name="peakCal" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      </>)}
    </div>
  );
};

export default HeartRateChart;
